import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { SizeCode } from 'entities/SizeCode';
import Konva from 'konva';
import { Layer } from 'konva/lib/Layer';
import { KonvaEventObject } from 'konva/lib/Node';
import { MutableRefObject, useCallback, useRef, useState } from 'react';
import { PIXELS_PER_METER } from '../../model/constants/mapScale';
import { getSnapStepPixels } from '../../model/selectors/getSnapStepPixels';
import { warehouseMapConstructorActions } from '../../model/slice/warehouseMapConstructorSlice';
import { Shape, ShapeType, WarehouseMapTool } from '../../model/types';
import { assignNearestSizeCode } from '../helpers/assignNearestSizeCode';
import { getNewSelectedAreaSize } from '../helpers/getNewSelectedAreaSize';
import { getRelativeCursorPosition } from '../helpers/getRelativeCursorPosition';
import { shapeSizing } from '../helpers/shapeSizing';

interface HookArgs {
  activeTool: WarehouseMapTool;
  transformerRef: MutableRefObject<Nullable<Konva.Transformer>>;
  floorId: string;
  availableSizeCodes: SizeCode[] | undefined;
}

interface HookApi {
  previewLayerRef: MutableRefObject<Nullable<Layer>>;
  selectedArea: SelectedArea;
  lastCanvasCursorRef: MutableRefObject<Nullable<{ x: number; y: number }>>;
  onMouseDown: (e: KonvaEventObject<MouseEvent>) => void;
  onMouseMove: (e: KonvaEventObject<MouseEvent>) => void;
  onMouseUp: (e: KonvaEventObject<MouseEvent>) => void;
  onMouseLeave: () => void;
  /** Destroy the current non-polygon shape preview and reset drawing state. */
  cancelPreview: () => void;
  /** True when a non-polygon shape is currently being previewed (dragged). */
  isPreviewingNonPolygon: () => boolean;
}

interface SelectedArea {
  x: number;
  y: number;
  width: number;
  height: number;
  startX: number;
  startY: number;
  visible: boolean;
}

const initialSelectedArea: SelectedArea = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  startX: 0,
  startY: 0,
  visible: false,
};

export const useMouseArea = ({ activeTool, transformerRef, floorId, availableSizeCodes }: HookArgs): HookApi => {
  const [selectedArea, setSelectedArea] = useState<SelectedArea>(initialSelectedArea);

  const dispatch = useAppDispatch();

  const snapStepPixels = useAppSelector(getSnapStepPixels);

  const shapePreview = useRef<Nullable<Shape>>(null);
  const previewLayerRef = useRef<Nullable<Konva.Layer>>(null);
  const isMouseDown = useRef<boolean>(false);
  const isShapeDragging = useRef<boolean>(false);
  const selectionStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  // Last known cursor position in world (canvas-space) coordinates.
  const lastCanvasCursorRef = useRef<Nullable<{ x: number; y: number }>>(null);

  const cancelPreview = useCallback((): void => {
    const shape = shapePreview.current;

    if (!shape) return;

    const node = previewLayerRef.current?.findOne(`#${shape.id}`);

    node?.destroy();
    previewLayerRef.current?.batchDraw();
    shapePreview.current = null;
    isMouseDown.current = false;
    isShapeDragging.current = false;
    setSelectedArea(initialSelectedArea);
  }, []);

  const isPreviewingNonPolygon = useCallback((): boolean => shapePreview.current !== null, []);

  const onMouseDown = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      if (e.evt.button === 1) return; // prevent drawing with mouse wheel click

      isMouseDown.current = true;

      if (activeTool === WarehouseMapTool.GRAB) return;

      const stage = e.target.getStage();
      const cursorPosition = getRelativeCursorPosition(stage);

      if (!cursorPosition) return;

      if (e.target !== stage && activeTool === WarehouseMapTool.POINTER && !(e.target instanceof Konva.Image)) {
        const shapeId = e.target.attrs.id || e.target.findAncestor('.shape')?.attrs.id;
        const selectedIds =
          transformerRef.current
            ?.nodes()
            .map((node) => node.attrs?.id)
            .filter((id): id is string => Boolean(id)) || [];
        const isMultiSelected = selectedIds.length > 1;
        const isClickedSelectedShape = Boolean(shapeId && selectedIds.includes(shapeId));
        isShapeDragging.current = true;

        if (shapeId) {
          if (e.evt.shiftKey) {
            dispatch(warehouseMapConstructorActions.addShapeToSelection(shapeId));
          } else if (e.evt.ctrlKey || e.evt.metaKey) {
            dispatch(warehouseMapConstructorActions.toggleShapeSelection(shapeId));
          } else if (!isMultiSelected || !isClickedSelectedShape) {
            dispatch(warehouseMapConstructorActions.selectShape(shapeId));
          }
        }

        return;
      } else {
        dispatch(warehouseMapConstructorActions.unselectShapes());
      }

      if (activeTool === WarehouseMapTool.TEXT) {
        const shapeId = crypto.randomUUID();
        const shape: Shape = {
          id: shapeId,
          floorId,
          type: ShapeType.TEXT,
          label: 'Text',
          fontSize: 16,
          scaleX: 1,
          scaleY: 1,
          ...cursorPosition,
        };
        dispatch(warehouseMapConstructorActions.appendShape(shape));

        return;
      }

      const selectedArea = {
        width: 0,
        height: 0,
        startX: cursorPosition.x,
        startY: cursorPosition.y,
        visible: activeTool === WarehouseMapTool.POINTER,
        ...cursorPosition,
      };

      selectionStartRef.current = { x: cursorPosition.x, y: cursorPosition.y };
      setSelectedArea(selectedArea);

      let shape: Nullable<Shape> = null;

      if (activeTool === WarehouseMapTool.RECTANGLE) {
        shape = {
          id: crypto.randomUUID(),
          floorId,
          type: ShapeType.RECTANGLE,
          fill: '#F3F6F9',
          stroke: '#788593',
          strokeWidth: 1,
          cornerRadius: 5,
          width: 0,
          height: 0,
          scaleX: 1,
          scaleY: 1,
          label: 'Label',
          ...cursorPosition,
        };
      }

      if (activeTool === WarehouseMapTool.CIRCLE) {
        shape = {
          id: crypto.randomUUID(),
          floorId,
          type: ShapeType.CIRCLE,
          fill: '#F3F6F9',
          stroke: '#788593',
          strokeWidth: 1,
          radiusX: 0,
          radiusY: 0,
          scaleX: 1,
          scaleY: 1,
          label: 'Label',
          ...cursorPosition,
        };
      }

      if (activeTool === WarehouseMapTool.PENCIL) {
        shape = {
          id: crypto.randomUUID(),
          floorId,
          type: ShapeType.LINE,
          fill: 'transparent',
          stroke: '#788593',
          strokeWidth: 3,
          scaleX: 1,
          scaleY: 1,
          points: [cursorPosition.x, cursorPosition.y],
          ...cursorPosition,
        };
      }

      if (activeTool === WarehouseMapTool.LINE) {
        shape = {
          id: crypto.randomUUID(),
          floorId,
          type: ShapeType.LINE,
          fill: 'transparent',
          stroke: '#788593',
          strokeWidth: 4,
          points: [cursorPosition.x, cursorPosition.y],
          scaleX: 1,
          scaleY: 1,
          ...cursorPosition,
        };
      }

      if (!shape) return;

      shapePreview.current = shape;

      switch (activeTool) {
        case WarehouseMapTool.RECTANGLE:
          previewLayerRef.current?.add(new Konva.Rect(shape));
          break;
        case WarehouseMapTool.CIRCLE:
          previewLayerRef.current?.add(new Konva.Ellipse({ ...shape, radiusX: 0, radiusY: 0, id: shape.id }));
          break;
        case WarehouseMapTool.PENCIL:
          previewLayerRef.current?.add(new Konva.Line({ ...shape, x: 0, y: 0, points: [shape.x, shape.y] }));
          break;
        case WarehouseMapTool.LINE:
          previewLayerRef.current?.add(new Konva.Line({ ...shape, x: 0, y: 0, points: [shape.x, shape.y] }));
          break;
        default:
          break;
      }
    },
    [activeTool, dispatch, floorId, transformerRef],
  );

  const onMouseMove = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      const stage = e.target.getStage();
      const cursorPosition = getRelativeCursorPosition(stage);

      if (cursorPosition) {
        lastCanvasCursorRef.current = { x: cursorPosition.x, y: cursorPosition.y };
      }

      if (!isMouseDown.current || isShapeDragging.current) return;

      if (!cursorPosition) return;

      const { width, height, x, y } = getNewSelectedAreaSize(cursorPosition, selectionStartRef.current);

      const rectSelection = shapeSizing.getRectSize({ width, height }, { x, y }, snapStepPixels);

      if (activeTool === WarehouseMapTool.POINTER) {
        setSelectedArea((prevState) => ({ ...prevState, ...rectSelection }));
        dispatch(warehouseMapConstructorActions.selectShapesInArea(rectSelection));

        return;
      }

      const shape = shapePreview.current;
      const shapeToEdit = previewLayerRef.current?.findOne(`#${shape?.id}`);

      if (!shapeToEdit || !shape) return;

      if (activeTool === WarehouseMapTool.RECTANGLE) {
        shapeToEdit.setAttrs(rectSelection);
        shapePreview.current = { ...shape, ...rectSelection };
      }

      if (activeTool === WarehouseMapTool.CIRCLE) {
        const circleSelection = shapeSizing.getEllipseSize({ width, height }, { x, y }, snapStepPixels);

        shapeToEdit.setAttrs(circleSelection);
        shapePreview.current = { ...shape, ...circleSelection };
      }

      if (activeTool === WarehouseMapTool.PENCIL && shape.type === ShapeType.LINE) {
        const points = shape.points.concat([cursorPosition.x, cursorPosition.y]);

        shape.points = points;
        shapeToEdit.setAttrs({ points });
      }

      if (activeTool === WarehouseMapTool.LINE && shape.type === ShapeType.LINE) {
        const points = [shape.points[0], shape.points[1], cursorPosition.x, cursorPosition.y];

        shape.points = points;
        shapeToEdit.setAttrs({ points });
      }

      previewLayerRef.current?.batchDraw();
    },
    [activeTool, dispatch, snapStepPixels],
  );

  const onMouseUp = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      isMouseDown.current = false;
      isShapeDragging.current = false;

      if (activeTool === WarehouseMapTool.RECTANGLE) {
        const shape = shapePreview.current;

        const cleanupRectanglePreview = (): void => {
          if (!shape) return;
          const shapeNode = previewLayerRef.current?.findOne(`#${shape.id}`);

          shapeNode?.destroy();
          previewLayerRef.current?.batchDraw();
          shapePreview.current = null;
        };

        if (!shape || !('width' in shape) || !('height' in shape)) {
          cleanupRectanglePreview();
          selectionStartRef.current = { x: 0, y: 0 };
          setSelectedArea(initialSelectedArea);

          return;
        }

        const isOnePixel = shape.width === 0 || shape.height === 0;
        const shapeWithSizeCode = !isOnePixel ? assignNearestSizeCode(shape, availableSizeCodes, PIXELS_PER_METER) : null;

        cleanupRectanglePreview();

        if (!isOnePixel && shapeWithSizeCode && 'sizeCodeId' in shapeWithSizeCode && shapeWithSizeCode.sizeCodeId) {
          dispatch(warehouseMapConstructorActions.appendShape(shapeWithSizeCode));
        }

        selectionStartRef.current = { x: 0, y: 0 };
        setSelectedArea(initialSelectedArea);

        return;
      }

      if (activeTool !== WarehouseMapTool.POINTER && activeTool !== WarehouseMapTool.GRAB) {
        const shape = shapePreview.current;

        if (!shape) return;

        const shapeToRemove = previewLayerRef.current?.findOne(`#${shape.id}`);

        const preventOnePixelShapeDrawing = (): boolean => {
          if ('points' in shape) {
            return shape.points.length === 2;
          }

          if ('width' in shape && 'height' in shape) {
            return shape.width === 0 || shape.height === 0;
          }

          if ('radiusX' in shape && 'radiusY' in shape) {
            return shape.radiusX === 0 || shape.radiusY === 0;
          }

          return false;
        };

        if (!preventOnePixelShapeDrawing()) {
          shapeToRemove?.destroy();
          previewLayerRef.current?.batchDraw();
          const shapeWithSizeCode = assignNearestSizeCode(shape, availableSizeCodes, PIXELS_PER_METER);

          dispatch(warehouseMapConstructorActions.appendShape(shapeWithSizeCode));
          shapePreview.current = null;
        } else {
          shapeToRemove?.destroy();
          shapePreview.current = null;
        }
      }

      selectionStartRef.current = { x: 0, y: 0 };
      setSelectedArea(initialSelectedArea);
    },
    [activeTool, availableSizeCodes, dispatch],
  );

  const onMouseLeave = useCallback(() => {
    lastCanvasCursorRef.current = null;

    const shape = shapePreview.current;

    if (!shape) return;

    const shapeToRemove = previewLayerRef.current?.findOne(`#${shape.id}`);

    shapeToRemove?.destroy();
    shapePreview.current = null;
    selectionStartRef.current = { x: 0, y: 0 };
    setSelectedArea(initialSelectedArea);
    isShapeDragging.current = false;
    isMouseDown.current = false;
  }, []);

  return {
    selectedArea,
    previewLayerRef,
    lastCanvasCursorRef,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    cancelPreview,
    isPreviewingNonPolygon,
  };
};
